import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAvailability } from '@/features/calls/hooks/useAvailability';
import { FavoriteButton } from '@/features/favorites/components/FavoriteButton';
import { resolveUploadUrl } from '@/lib/uploads';
import { useConversations, useMessages, useSendMessage, useMarkAsRead, useMarkAsUnread } from '../hooks/useChat';
import { useToggleStar } from '../hooks/useStarredMessages';
import { useChatSocket } from '../hooks/useChatSocket';
import { ConversationList } from './ConversationList';
import { MessageThread } from './MessageThread';
import { MessageInput } from './MessageInput';

export function ChatPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getStatus } = useAvailability();
  const professionalId = searchParams.get('professional');
  // Professional-initiated only: target a client by userId when there's no
  // conversation yet (e.g. "Scrivi" from the consultation history).
  const userId = searchParams.get('user');

  const [mobileShowThread, setMobileShowThread] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const { data: conversations = [], isLoading: convsLoading } = useConversations();
  const { data: messagesData, isLoading: msgsLoading } = useMessages(conversationId);
  const sendMessage = useSendMessage();
  const markAsRead = useMarkAsRead();
  const markAsUnread = useMarkAsUnread();
  const isSuperAdmin = user?.role === 'super_admin';
  // Messaggi preferiti: solo i partecipanti (cliente/consulente), non gli admin.
  const canStar = user?.role === 'user' || user?.role === 'professional';
  const toggleStar = useToggleStar(conversationId);

  const { isTyping, emitTypingStart, emitTypingStop } = useChatSocket(conversationId);

  // When ?professional=<id>, find existing conversation or stay for new
  useEffect(() => {
    if (!professionalId || convsLoading) return;
    const existing = conversations.find(
      (c) => String(c.professionalId) === professionalId
    );
    if (existing) {
      navigate(`/my/chat/${existing.id}`, { replace: true });
    }
  }, [professionalId, conversations, convsLoading, navigate]);

  // When ?user=<id> (professional targeting a client), find existing
  // conversation or stay for new.
  useEffect(() => {
    if (!userId || convsLoading) return;
    const existing = conversations.find((c) => String(c.userId) === userId);
    if (existing) {
      navigate(`/my/chat/${existing.id}`, { replace: true });
    }
  }, [userId, conversations, convsLoading, navigate]);

  // Mark conversation as read when opened
  useEffect(() => {
    if (conversationId) {
      markAsRead.mutate(conversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // When a conversation is selected — or a brand-new one is being started via
  // ?professional=<id> (no conversationId yet) — show the thread on mobile.
  // Without the professionalId case the new-conversation thread stays hidden
  // behind the conversation list on mobile, so the chat looks broken.
  useEffect(() => {
    if (conversationId || professionalId || userId) {
      setMobileShowThread(true);
    }
  }, [conversationId, professionalId, userId]);

  // Clear send error after 5 seconds
  useEffect(() => {
    if (!sendError) return;
    const timer = setTimeout(() => setSendError(null), 5000);
    return () => clearTimeout(timer);
  }, [sendError]);

  const handleSend = useCallback(
    (content: string) => {
      setSendError(null);
      const payload = conversationId
        ? { conversationId, content }
        : professionalId
          ? { professionalId, content }
          : userId
            ? { userId, content }
            : null;

      if (!payload) return;

      sendMessage.mutate(payload, {
        onError: (error: unknown) => {
          const msg = error instanceof Error ? error.message : 'Errore nell\'invio del messaggio';
          // Try to extract API error message
          const apiError = (error as { response?: { data?: { error?: { message?: string } } } })
            ?.response?.data?.error?.message;
          setSendError(apiError || msg);
        },
      });
    },
    [conversationId, professionalId, userId, sendMessage]
  );

  const handleTyping = useCallback(() => {
    if (conversationId) emitTypingStart(conversationId);
  }, [conversationId, emitTypingStart]);

  const handleTypingStop = useCallback(() => {
    if (conversationId) emitTypingStop(conversationId);
  }, [conversationId, emitTypingStop]);

  const handleBack = () => {
    setMobileShowThread(false);
    navigate('/my/chat');
  };

  const messages = messagesData?.data ?? [];

  // Resolve participant info for the active conversation
  const activeConversation = conversations.find((c) => c.id === conversationId);

  const isCurrentUserTheUser = activeConversation
    ? user?.id === String(activeConversation.userId)
    : !userId; // a fresh ?user=<id> chat is started BY the professional

  // Check consultant availability for this conversation
  const activeProfessionalId = activeConversation
    ? String(activeConversation.professionalId)
    : professionalId ?? undefined;
  const consultantStatus = activeProfessionalId ? getStatus(activeProfessionalId) : 'offline';

  // Determine if input should be disabled and why
  let inputDisabled = !conversationId && !professionalId && !userId;
  let inputDisabledReason: string | undefined;

  if (!inputDisabled && consultantStatus !== 'offline') {
    // Consultant is online or busy — no messages allowed
    inputDisabled = true;
    inputDisabledReason = consultantStatus === 'online'
      ? 'Il consulente è online. I messaggi sono disponibili solo quando è offline.'
      : 'Il consulente è in chiamata. I messaggi sono disponibili solo quando è offline.';
  }

  if (!inputDisabled && !isCurrentUserTheUser && activeConversation) {
    // Professional side, existing conversation: check if there are user
    // messages to reply to. A brand-new ?user=<id> chat (no activeConversation
    // yet) skips this — the backend already verified eligibility to write first.
    const hasUserMessages = messages.some(
      (m) => String(m.senderId) === String(activeConversation.userId)
    );
    if (!hasUserMessages && messages.length === 0) {
      inputDisabled = true;
      inputDisabledReason = 'In attesa del primo messaggio dall\'utente...';
    }
  }

  const otherParty = activeConversation
    ? isCurrentUserTheUser
      ? {
          name: `${activeConversation.professional?.firstName ?? ''} ${activeConversation.professional?.lastName ?? ''}`.trim() || 'Consulente',
          title: activeConversation.professional?.title,
          avatar: activeConversation.professional?.avatar,
        }
      : {
          name: `${activeConversation.user?.firstName ?? ''} ${activeConversation.user?.lastName ?? ''}`.trim() || 'Utente',
          title: undefined,
          avatar: activeConversation.user?.avatar,
        }
    : (professionalId || userId)
      ? { name: 'Nuova conversazione', title: undefined, avatar: undefined }
      : undefined;

  const participants = (activeConversation || professionalId || userId)
    ? {
        myName: 'Tu',
        otherName: otherParty?.name || '',
        otherAvatar: resolveUploadUrl(otherParty?.avatar),
        otherInitial: otherParty?.name?.[0]?.toUpperCase() ?? '?',
      }
    : undefined;

  const typingActive = isTyping(conversationId ?? '');

  return (
    <div className="mx-auto flex h-[calc(100vh-5rem)] max-w-7xl flex-col px-4 py-4 sm:px-6 lg:px-8">
      {/* Chat container — fills available space */}
      <div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-lg shadow-neutral-200/60">
        {/* Conversation list — left panel */}
        <div
          className={`flex w-full flex-col border-r border-neutral-200 md:w-[320px] md:shrink-0 ${
            mobileShowThread ? 'hidden md:flex' : 'flex'
          }`}
        >
          <div className="flex h-[3.75rem] items-center gap-2 border-b border-neutral-200 px-5">
            <svg className="h-5 w-5 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
            </svg>
            <h2 className="text-sm font-semibold text-neutral-800">Chat</h2>
            {conversations.length > 0 && (
              <span className="ml-auto rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-medium tabular-nums text-neutral-400">
                {conversations.length}
              </span>
            )}
          </div>
          <ConversationList
            conversations={conversations}
            activeId={conversationId}
            isLoading={convsLoading}
          />
        </div>

        {/* Message thread — right panel */}
        <div
          className={`flex flex-1 flex-col ${
            mobileShowThread ? 'flex' : 'hidden md:flex'
          }`}
        >
          {/* Thread header */}
          {otherParty ? (
            <div className="flex h-[3.75rem] items-center gap-3 border-b border-neutral-200 px-5">
              {/* Mobile back button */}
              <button
                onClick={handleBack}
                className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-900 md:hidden"
                aria-label="Torna alla lista"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Avatar */}
              <div className="relative h-9 w-9">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 ring-2 ring-neutral-200">
                  <img src="/images/logo-navy.png" alt="" className="h-5 w-5 object-contain opacity-60" />
                </div>
                {otherParty.avatar && (
                  <img
                    src={resolveUploadUrl(otherParty.avatar)}
                    alt={otherParty.name}
                    className="absolute inset-0 h-9 w-9 rounded-full object-cover ring-2 ring-neutral-200"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
              </div>

              {/* Name and status */}
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-neutral-900">{otherParty.name}</p>
                {typingActive ? (
                  <p className="text-[11px] font-medium text-brand-teal">sta scrivendo...</p>
                ) : otherParty.title ? (
                  <p className="truncate text-[11px] text-neutral-400">{otherParty.title}</p>
                ) : null}
              </div>

              {/* Consultant status indicator */}
              {isCurrentUserTheUser && activeProfessionalId && (
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  consultantStatus === 'online'
                    ? 'bg-emerald-500/15 text-emerald-600'
                    : consultantStatus === 'busy'
                      ? 'bg-amber-500/15 text-amber-600'
                      : 'bg-neutral-100 text-neutral-400'
                }`}>
                  {consultantStatus === 'online' ? 'Online' : consultantStatus === 'busy' ? 'Occupato' : 'Offline'}
                </span>
              )}

              {/* Favorite toggle — user side only */}
              {isCurrentUserTheUser && activeProfessionalId && (
                <FavoriteButton professionalId={activeProfessionalId} size="sm" variant="light" />
              )}
            </div>
          ) : (
            <div className="h-[3.75rem] border-b border-neutral-200" />
          )}

          <MessageThread
            messages={messages}
            isLoading={msgsLoading}
            isTyping={typingActive}
            conversationId={conversationId ?? ((professionalId || userId) ? '__new__' : undefined)}
            participants={participants}
            customerUserId={activeConversation ? String(activeConversation.userId) : undefined}
            onMarkUnread={
              isSuperAdmin && conversationId
                ? () => markAsUnread.mutate(conversationId)
                : undefined
            }
            onToggleStar={
              canStar && conversationId
                ? (messageId, starred) => toggleStar.mutate({ messageId, starred })
                : undefined
            }
          />

          {/* Send error banner */}
          {sendError && (
            <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-600">
              {sendError}
            </div>
          )}

          <MessageInput
            onSend={handleSend}
            onTyping={handleTyping}
            onTypingStop={handleTypingStop}
            disabled={inputDisabled}
            disabledReason={inputDisabledReason}
            conversationId={conversationId}
          />
        </div>
      </div>
    </div>
  );
}
